import React, { useEffect, useState } from 'react';
import { ClipboardCheck } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';

/**
 * ¿ESTE CLAIM LLEVABA SUPLEMENTO? — la respuesta, y la llave del cierre.
 *
 * El claim no se puede cerrar mientras esto diga «pendiente» (revisionGuard del backend). Esta
 * tarjeta va PRIMERA en la pestaña porque es lo que frena el cierre: enterarse del candado recién
 * cuando el desplegable de etapa rebota es la peor forma de conocer una regla.
 *
 * «No aplica» pide motivo del catálogo, acá y en la base. Es lo único que evita que ese estado se
 * convierta en el botón de sacarse el claim de encima.
 */

type Revision = {
  status: 'pending' | 'in_progress' | 'submitted' | 'approved' | 'not_applicable';
  not_applicable_reason: string | null;
  by_name: string | null;
  at: string | null;
  cierra: boolean;
};

const MOTIVOS = ['carrier_estimate_complete', 'damage_not_covered', 'client_declined', 'already_repaired', 'other'] as const;

/** Verde = contestado, gris = en curso, naranja = pendiente (que es lo que frena el cierre). */
function color(s: Revision['status']): string {
  if (s === 'pending') return 'var(--orange)';
  if (s === 'in_progress') return 'var(--muted)';
  return 'var(--green)';
}

export function SupplementReview({ claimId }: { claimId: string }) {
  const { t } = useI18n();
  const [rev, setRev] = useState<Revision | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [motivo, setMotivo] = useState<string>('carrier_estimate_complete');

  const cargar = () => {
    api.get<Revision>(`/api/claims/${claimId}/supplement-review`)
      .then(setRev)
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  };
  useEffect(cargar, [claimId]);

  const fijar = async (status: Revision['status']) => {
    setGuardando(true); setErr(null);
    try {
      await api.post(`/api/claims/${claimId}/supplement-review`, {
        status, reason: status === 'not_applicable' ? motivo : null,
      });
      cargar();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setGuardando(false); }
  };

  if (!rev) return null;
  const pend = rev.status === 'pending';

  return (
    <div className="card" style={{ padding: 12, borderLeft: `3px solid ${color(rev.status)}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <ClipboardCheck size={15} aria-hidden />
        <span style={{ fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'] }}>{t('sr.title')}</span>
        <span style={{ marginLeft: 'auto', color: color(rev.status), fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'] }}>
          {t(`sr.st.${rev.status}`)}
        </span>
      </div>

      <p className="muted" style={{ margin: '0 0 8px', fontSize: 'var(--fs-2xs)' }}>
        {pend ? t('sr.helpPending') : t('sr.helpDone', {
          quien: rev.by_name ?? '—',
          cuando: rev.at ? rev.at.slice(0, 10) : '—',
          motivo: rev.not_applicable_reason ? ` · ${t(`sr.re.${rev.not_applicable_reason}`)}` : '',
        })}
      </p>

      {pend && rev.cierra && (
        <p style={{ margin: '0 0 8px', fontSize: 'var(--fs-2xs)', color: 'var(--orange)' }}>{t('sr.blocking')}</p>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        {pend && (
          <button className="btn btn-sm" disabled={guardando} onClick={() => fijar('in_progress')}>{t('sr.markInProgress')}</button>
        )}
        {rev.status !== 'submitted' && rev.status !== 'approved' && (
          <button className="btn btn-sm" disabled={guardando} onClick={() => fijar('submitted')}>{t('sr.markSubmitted')}</button>
        )}
        {rev.status !== 'not_applicable' ? (
          <>
            <select className="input input-sm" value={motivo} onChange={(e) => setMotivo(e.target.value)} aria-label={t('sr.reasonLabel')}>
              {MOTIVOS.map((m) => <option key={m} value={m}>{t(`sr.re.${m}`)}</option>)}
            </select>
            <button className="btn btn-sm" disabled={guardando} onClick={() => fijar('not_applicable')}>{t('sr.markNa')}</button>
          </>
        ) : (
          <button className="btn btn-sm" disabled={guardando} onClick={() => fijar('pending')}>{t('sr.undo')}</button>
        )}
      </div>

      {err && <p style={{ margin: '8px 0 0', fontSize: 'var(--fs-2xs)', color: 'var(--red)' }}>{err}</p>}
    </div>
  );
}
